import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { IoClose } from 'react-icons/io5';
import { useEffect } from 'react';
import { FiLoader, FiDownload, FiEdit2, FiEye } from 'react-icons/fi';
import { renderAsync } from 'docx-preview';
import mammoth from 'mammoth';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';

const modalRoot = document.getElementById('modal-root');

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  headerActions,
  size = '2xl',
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Use ReactDOM.createPortal to render the modal outside of the current component hierarchy
  return ReactDOM.createPortal(
    // These classes are now applied to the viewport, guaranteeing centering
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm transition-opacity"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className={`relative flex h-[90vh] max-h-[90vh] w-[95vw] max-w-7xl flex-col rounded-xl border border-zinc-700
                     bg-zinc-900 shadow-2xl max-w-${size}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* --- CHANGES ARE HERE --- */}
        <div className="flex items-center justify-between border-b border-slate-700 p-4 md:p-6">
          <h3 className="text-xl font-semibold text-white">{title}</h3>

          {/* Container for all buttons on the right */}
          <div className="flex items-center gap-4">
            {/* 1. Render the custom action buttons passed via props */}
            {headerActions}

            {/* 2. The original close button */}
            <button
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 transition-colors hover:bg-slate-700 hover:text-white"
            >
              <IoClose size={24} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto p-4 md:p-6">{children}</div>
      </div>
    </div>,
    modalRoot, // The target DOM node for the portal
  );
};

//Docs preview
const DocxPreviewModal = ({ file, isOpen, onClose, filename }) => {
  const previewRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (isOpen && file && previewRef.current && !isEditing) {
      setIsLoading(true);
      setPreviewError(null);
      const reader = new FileReader();
      const timeoutId = setTimeout(() => {
        reader.abort();
        setIsLoading(false);
        setPreviewError('Document took too long to load.');
      }, 30000);
      reader.onload = function (event) {
        clearTimeout(timeoutId);
        const arrayBuffer = event.target.result;
        renderAsync(arrayBuffer, previewRef.current, null, {
          inWrapper: true,
          ignoreHeight: false,
        })
          .then(() => setIsLoading(false))
          .catch(() => {
            setIsLoading(false);
            setPreviewError('Failed to render document preview.');
          });
      };
      reader.onerror = () => {
        clearTimeout(timeoutId);
        setIsLoading(false);
        setPreviewError('Failed to read document file.');
      };
      reader.readAsArrayBuffer(file);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, file, isEditing]);

  const handleEditToggle = async () => {
    if (!isEditing) {
      try {
        setIsLoading(true);
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({
          arrayBuffer: arrayBuffer,
        });
        setHtmlContent(result.value);
        setIsEditing(true);
      } catch (err) {
        toast.error('Failed to extract document text for editing.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleHtmlChange = (val) => {
    setHtmlContent(val);
    setHasChanges(true);
  };

  const handleInternalDownload = () => {
    if (!file) return;

    if (isEditing && hasChanges) {
      const wordHtml = `
               <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
               <head><meta charset='utf-8'><title>${file?.name || 'Document'}</title></head>
               <body>${htmlContent}</body>
               </html>
             `;
      const blob = new Blob(['\ufeff', wordHtml], {
        type: 'application/msword',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        (file?.name ? file.name.replace('.docx', '') : 'edited_doc') + '.doc',
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', file.name || 'template.docx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const modalActions = (
    <div className="flex items-center gap-2">
      <button
        onClick={handleEditToggle}
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${isEditing ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-zinc-800 text-zinc-200 hover:bg-zinc-700'}`}
        title="Toggle Edit Mode"
      >
        {isEditing ? (
          <FiEye className="h-4 w-4" />
        ) : (
          <FiEdit2 className="h-4 w-4" />
        )}
        <span className="hidden sm:inline">
          {isEditing ? 'View Mode' : 'Edit Doc'}
        </span>
      </button>
      <button
        onClick={handleInternalDownload}
        className="flex items-center gap-2 rounded-lg bg-zinc-800 px-3 py-2 text-sm text-zinc-200 transition-colors hover:bg-zinc-700"
        title="Download file"
      >
        <FiDownload className="h-4 w-4" />
        <span className="hidden sm:inline">
          {isEditing && hasChanges ? 'Save / Download' : 'Download'}
        </span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={file?.name || 'Document Preview'}
      size="4xl"
      headerActions={modalActions}
    >
      {isLoading && (
        <div className="flex h-96 items-center justify-center">
          <FiLoader className="animate-spin text-4xl text-[#5652CF]" />
        </div>
      )}

      {previewError && !isEditing && (
        <div className="flex h-96 items-center justify-center">
          <p className="text-red-400">{previewError}</p>
        </div>
      )}

      {isEditing ? (
        <div className="flex h-full min-h-[500px] flex-col overflow-hidden rounded-lg bg-white text-black">
          <ReactQuill
            theme="snow"
            value={htmlContent}
            onChange={handleHtmlChange}
            className="flex-1 overflow-y-auto"
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                [{ font: [] }],
                [{ color: [] }, { background: [] }],
                [{ align: [] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [
                  { list: 'ordered' },
                  { list: 'bullet' },
                  { indent: '-1' },
                  { indent: '+1' },
                ],
                ['link', 'image', 'video'],
                ['clean'],
              ],
            }}
          />
        </div>
      ) : (
        <div
          ref={previewRef}
          className={`docx-preview-container ${isLoading ? 'hidden' : ''}`}
        ></div>
      )}
    </Modal>
  );
};

export default DocxPreviewModal;
